import { useState, useEffect } from 'react';
import {
  collection,
  query,
  where,
  orderBy,
  onSnapshot,
  getDocs,
  addDoc,
  updateDoc,
  deleteDoc,
  doc,
  Timestamp,
} from 'firebase/firestore';
import { db } from '../services/firebase';
import type { Invoice, InvoiceStatus } from '../models/Invoice';
import { useAuth } from '../contexts/AuthContext';
import { UserRole } from '../models/User';
import { logError } from '../../lib/utils';

interface UseInvoicesOptions {
  organizationId?: string | null;
  clientId?: string | null;
  status?: InvoiceStatus;
  dateRange?: {
    start: Date;
    end: Date;
  };
}

export function useInvoices(options: UseInvoicesOptions = {}) {
  const [invoices, setInvoices] = useState<Invoice[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const { currentUser } = useAuth();

  const invoiceDateRangeStart = options.dateRange?.start?.getTime();
  const invoiceDateRangeEnd = options.dateRange?.end?.getTime();

  useEffect(() => {
    setLoading(true);

    const constraints: any[] = [];

    if (options.organizationId) {
      constraints.push(where('organizationId', '==', options.organizationId));
    } else if (currentUser?.role !== UserRole.SUPER_ADMIN && currentUser?.organizationId) {
      constraints.push(where('organizationId', '==', currentUser.organizationId));
    }

    if (options.clientId) {
      constraints.push(where('clientId', '==', options.clientId));
    }

    if (options.status) {
      constraints.push(where('status', '==', options.status));
    }

    if (options.dateRange) {
      constraints.push(where('date', '>=', Timestamp.fromDate(options.dateRange.start)));
      constraints.push(where('date', '<=', Timestamp.fromDate(options.dateRange.end)));
    }

    // Don't add orderBy if we have where clauses - we'll sort in JavaScript
    const hasWhereClauses = constraints.length > 0;
    if (!hasWhereClauses) {
      constraints.push(orderBy('date', 'desc'));
    }

    let q;
    let needsSorting = false;
    if (constraints.length > 0) {
      q = query(collection(db, 'invoices'), ...constraints);
      needsSorting = hasWhereClauses;
    } else if (currentUser?.role === UserRole.SUPER_ADMIN) {
      q = query(collection(db, 'invoices'), orderBy('date', 'desc'));
      // No sorting needed - Firestore handles it
    } else if (currentUser?.organizationId) {
      // Filter by organizationId - remove orderBy to avoid index requirement
      q = query(
        collection(db, 'invoices'),
        where('organizationId', '==', currentUser.organizationId)
      );
      needsSorting = true;
    } else {
      setInvoices([]);
      setLoading(false);
      return;
    }

    const unsubscribe = onSnapshot(
      q,
      (snapshot) => {
        const invoicesData = snapshot.docs.map((doc) => {
          const data = doc.data();
          return {
            id: doc.id,
            ...data,
            date: data.date?.toDate() || new Date(),
            dueDate: data.dueDate?.toDate() || new Date(),
            createdAt: data.createdAt?.toDate() || new Date(),
            updatedAt: data.updatedAt?.toDate() || new Date(),
          } as Invoice;
        });
        
        // Sort by date descending (newest first) if we removed orderBy
        if (needsSorting) {
          invoicesData.sort((a, b) => {
            const aTime = a.date.getTime();
            const bTime = b.date.getTime();
            return bTime - aTime; // Descending order
          });
        }
        
        setInvoices(invoicesData);
        setLoading(false);
        setError(null);
      },
      (err: unknown) => {
        logError('invoices', err, {
          source: 'useInvoices.subscription',
          organizationId: options.organizationId ?? currentUser?.organizationId,
          clientId: options.clientId,
          status: options.status,
          hasDateRange: Boolean(options.dateRange),
        });
        setError(err as Error);
        setLoading(false);
      }
    );

    return () => unsubscribe();
  }, [
    options.organizationId,
    options.clientId,
    options.status,
    options.dateRange,
    invoiceDateRangeStart,
    invoiceDateRangeEnd,
    currentUser?.role,
    currentUser?.organizationId,
  ]);

  const generateInvoiceNumber = async (organizationId: string): Promise<string> => {
    // Get all invoices for this organization and find the latest number
    // Remove orderBy to avoid index requirement - we'll find max in JavaScript
    const q = query(
      collection(db, 'invoices'),
      where('organizationId', '==', organizationId)
    );
    
    const snapshot = await getDocs(q);
    if (snapshot.empty) {
      return `INV-${new Date().getFullYear()}-001`;
    }
    
    // Find the invoice with the highest number
    let lastNumber = '';
    snapshot.docs.forEach((doc) => {
      const number = doc.data().number;
      if (number && (!lastNumber || number > lastNumber)) {
        lastNumber = number;
      }
    });
    
    if (!lastNumber) {
      return `INV-${new Date().getFullYear()}-001`;
    }
    
    const match = lastNumber.match(/INV-(\d+)-(\d+)/);
    if (match) {
      const year = parseInt(match[1]);
      const num = parseInt(match[2]);
      const currentYear = new Date().getFullYear();
      
      if (year === currentYear) {
        return `INV-${year}-${String(num + 1).padStart(3, '0')}`;
      } else {
        return `INV-${currentYear}-001`;
      }
    }
    
    return `INV-${new Date().getFullYear()}-001`;
  };

  const createInvoice = async (invoiceData: Omit<Invoice, 'id' | 'number' | 'createdAt' | 'updatedAt'>) => {
    try {
      const invoiceNumber = await generateInvoiceNumber(invoiceData.organizationId);
      
      const docRef = await addDoc(collection(db, 'invoices'), {
        ...invoiceData,
        number: invoiceNumber,
        date: Timestamp.fromDate(invoiceData.date),
        dueDate: Timestamp.fromDate(invoiceData.dueDate),
        createdAt: Timestamp.now(),
        updatedAt: Timestamp.now(),
      });
      return docRef.id;
    } catch (err: unknown) {
      logError('invoices', err, { source: 'useInvoices.createInvoice', organizationId: invoiceData.organizationId });
      throw err;
    }
  };

  const updateInvoice = async (invoiceId: string, updates: Partial<Invoice>) => {
    try {
      const invoiceRef = doc(db, 'invoices', invoiceId);
      const updateData: any = {
        ...updates,
        updatedAt: Timestamp.now(),
      };

      if (updates.date) {
        updateData.date = Timestamp.fromDate(updates.date);
      }
      if (updates.dueDate) {
        updateData.dueDate = Timestamp.fromDate(updates.dueDate);
      }

      await updateDoc(invoiceRef, updateData);
    } catch (err: unknown) {
      logError('invoices', err, { source: 'useInvoices.updateInvoice', invoiceId });
      throw err;
    }
  };

  const deleteInvoice = async (invoiceId: string) => {
    try {
      const invoiceRef = doc(db, 'invoices', invoiceId);
      await deleteDoc(invoiceRef);
    } catch (err: unknown) {
      logError('invoices', err, { source: 'useInvoices.deleteInvoice', invoiceId });
      throw err;
    }
  };

  const markAsPaid = async (invoiceId: string) => {
    return updateInvoice(invoiceId, { status: 'paid' as InvoiceStatus });
  };

  const markAsSent = async (invoiceId: string) => {
    return updateInvoice(invoiceId, { status: 'sent' as InvoiceStatus });
  };

  // Calculate totals
  const totalAmount = invoices.reduce((sum, invoice) => sum + invoice.total, 0);
  const paidAmount = invoices
    .filter((invoice) => invoice.status === 'paid')
    .reduce((sum, invoice) => sum + invoice.total, 0);
  const outstandingAmount = totalAmount - paidAmount;

  return {
    invoices,
    loading,
    error,
    createInvoice,
    updateInvoice,
    deleteInvoice,
    markAsPaid,
    markAsSent,
    totalAmount,
    paidAmount,
    outstandingAmount,
  };
}
